import { styled } from "@mui/material/styles";
import {
    Box,
    Grid,
    GridProps,
    IconButton,
    InternalStandardProps,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef } from "react";
import { CourseLogFirebaseEventSettings } from "./CourseStep";
import {
    CourseDetail,
    EventActions,
    EventCategories,
    EventNames,
} from "JS/Models";
import { useGlobalMedia } from "JS/React/Hooks/Media";
import { AppDocument } from "JS/React/Components/AppDocument";
import { RenderPrintProps, printPlugin } from "@react-pdf-viewer/print";
import { isIOS } from "react-device-detect";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { messaging } from "JS/Helpers/UserMessaging";

const ContainersGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(1),
}));

const DocumentGrid = styled(Grid)(() => ({
    overflow: "hidden",
}));

const IconStyled = styled(FontAwesomeIcon)(({ theme }) => ({
    color: theme.palette.grey[600],
    fontSize: "22px",
}));

const Grey600Icon = styled(FontAwesomeIcon)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

export interface PdfStepProps extends InternalStandardProps<GridProps> {
    mediaUrl: string;
    course: CourseDetail;
    onStepCompleted?: () => Promise<void>;
    onStepOpen: () => Promise<void>;
    onClose: () => void;
    handleLogFirebaseEvents: (
        type: "course" | "step" | "question",
        settings: CourseLogFirebaseEventSettings,
    ) => void;
}

export const PdfStep = (props: PdfStepProps) => {
    const {
        className,
        mediaUrl,
        course,
        onStepCompleted,
        handleLogFirebaseEvents,
        onStepOpen,
        onClose,
        ...rest
    } = props;

    const pdfWrapperRef = useRef(null);
    const { setIsZoomAllowed } = useGlobalMedia();
    const printPluginInstance = printPlugin();
    const { Print } = printPluginInstance;

    const onDocumentLoadSuccess = async () => {
        if (course?.contentType === "Course") {
            handleLogFirebaseEvents("step", {
                action: EventActions.STARTED,
                category: EventCategories.COURSE_STEP,
                eventNames: EventNames.COURSE_STEP_STARTED,
            });
            handleLogFirebaseEvents("step", {
                action: EventActions.DOCUMENT_VIEWED,
                category: EventCategories.COURSE_STEP,
                eventNames: EventNames.COURSE_STEP_DOCUMENT_VIEWED,
            });
        }
        if (course?.contentType === "GiftCourse") {
            handleLogFirebaseEvents("step", {
                action: EventActions.STARTED,
                category: EventCategories.GIFT_COURSE_STEP,
                eventNames: EventNames.GIFT_COURSE_STEP_STARTED,
            });
            handleLogFirebaseEvents("step", {
                action: EventActions.DOCUMENT_VIEWED,
                category: EventCategories.GIFT_COURSE_STEP,
                eventNames: EventNames.GIFT_COURSE_STEP_DOCUMENT_VIEWED,
            });
        }
        await onStepOpen();
        if (onStepCompleted) {
            await onStepCompleted();
        }
    };

    useEffect(() => {
        setIsZoomAllowed(true);

        return () => {
            setIsZoomAllowed(false);
        };
    }, []);

    return (
        <ContainersGrid {...rest} container className={className}>
            <Grid item xs={12} display="flex" justifyContent="space-between">
                {!isIOS && (
                    <Print>
                        {(props: RenderPrintProps) => (
                            <IconButton onClick={props.onClick}>
                                <IconStyled icon={["fas", "print"]} />
                            </IconButton>
                        )}
                    </Print>
                )}
                {isIOS && <Box />}
                <IconButton onClick={onClose}>
                    <Grey600Icon
                        style={{
                            fontSize: "30px",
                        }}
                        icon={["fas", "xmark"]}
                    />
                </IconButton>
            </Grid>
            <DocumentGrid item xs={12} ref={pdfWrapperRef}>
                {mediaUrl && (
                    <AppDocument
                        fileUrl={mediaUrl}
                        printPlugin={printPluginInstance}
                        onDocumentOpen={onDocumentLoadSuccess}
                        loader={
                            <Box display="flex" justifyContent="center">
                                <AppBackdropProgress
                                    open
                                    backdropText={messaging.loader.document}
                                />
                            </Box>
                        }
                    />
                )}
            </DocumentGrid>
        </ContainersGrid>
    );
};
